import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import {
  Home,
  Plus,
  Wallet,
  TrendingUp,
  ArrowUpRight,
  X,
  MapPin,
  Trash2,
} from "lucide-react";

const STRATEGIES = ["Buy & Hold", "BRRRR", "Fix & Flip", "Short-term Rental", "Other"];

const EMPTY = {
  property_address: "", city: "", state: "", zip_code: "",
  strategy: "Buy & Hold",
  purchase_price: "", rehab_cost: "", current_value: "",
  monthly_rent: "", monthly_expenses: "", mortgage_balance: "",
  purchased_at: "", notes: "",
};

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/portfolio"),
      api.get("/portfolio/summary"),
    ]);
    setItems(a.data);
    setSummary(b.data);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this property from your portfolio?")) return;
    await api.delete(`/portfolio/${id}`);
    fetchAll();
  };

  return (
    <div data-testid="portfolio-page">
      <div className="topbar">
        <div>
          <div className="crumb">Portfolio</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Your Portfolio</h1>
        </div>
        <button type="button" className="btn-money" onClick={() => { setEditing(null); setOpen(true); }} data-testid="portfolio-add-button">
          <Plus size={14} /> Add Property
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Properties" value={summary?.property_count ?? "—"} />
        <Stat label="Total Value" value={fmtMoney(summary?.total_value)} accent />
        <Stat label="Total Equity" value={fmtMoney(summary?.total_equity)} accent />
        <Stat label="Monthly Cash Flow" value={fmtMoney(summary?.monthly_cash_flow)} accent={summary?.monthly_cash_flow > 0} />
      </div>

      {summary && (
        <div className="card mb-6" data-testid="portfolio-appreciation">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="label-eyebrow">Appreciation</div>
              <div className="font-display font-bold text-2xl mt-1">
                {fmtMoney(summary.total_appreciation)} <span className="text-base text-[var(--ink-2)]">({fmtPct(summary.appreciation_pct)})</span>
              </div>
            </div>
            <div className="text-right">
              <div className="label-eyebrow">Annual Cash Flow</div>
              <div className="font-display font-bold text-2xl mt-1 text-[var(--primary)]">{fmtMoney(summary.annual_cash_flow)}</div>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty" data-testid="portfolio-empty">
          <Home size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-semibold mb-1">No properties in your portfolio yet</div>
          <div className="text-sm">Track holds, BRRRRs, and rentals — see your wealth compound over time.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <PropCard key={p.id} prop={p} onEdit={() => { setEditing(p); setOpen(true); }} onDelete={() => onDelete(p.id)} />
          ))}
        </div>
      )}

      {open && (
        <PortfolioModal
          initial={editing}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); fetchAll(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className={"value numeric " + (accent ? "text-[var(--primary)]" : "")}>{value}</div>
    </div>
  );
}

function PropCard({ prop, onEdit, onDelete }) {
  const m = prop.metrics || {};
  return (
    <div className="card hover:shadow-lg transition-shadow group cursor-pointer relative" onClick={onEdit} data-testid={`portfolio-card-${prop.id}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute top-3 right-3 btn-ghost opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`portfolio-delete-${prop.id}`}
      >
        <Trash2 size={14} />
      </button>
      <span
        className="chip mb-3"
        style={{ background: "rgba(16,185,129,0.1)", color: "var(--primary-dark)", borderColor: "rgba(16,185,129,0.3)" }}
      >
        {prop.strategy}
      </span>
      <h3 className="font-display font-bold text-lg leading-tight mb-1">{prop.property_address}</h3>
      <div className="text-sm text-[var(--ink-2)] flex items-center gap-1 mb-4">
        <MapPin size={12} /> {[prop.city, prop.state].filter(Boolean).join(", ")}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Mini label="Value" value={fmtMoney(prop.current_value)} />
        <Mini label="Equity" value={fmtMoney(m.equity)} accent />
        <Mini label="Monthly CF" value={fmtMoney(m.monthly_cash_flow)} accent={m.monthly_cash_flow > 0} />
        <Mini label="Cap Rate" value={fmtPct(m.cap_rate_pct)} />
      </div>
      {m.appreciation_pct !== 0 && (
        <div className="flex items-center gap-1 mt-4 pt-4 border-t border-[var(--line)] text-sm">
          {m.appreciation > 0 ? (
            <ArrowUpRight size={14} className="text-[var(--primary)]" />
          ) : (
            <ArrowUpRight size={14} className="text-[var(--warn)] rotate-180" />
          )}
          <span className="font-semibold text-[var(--ink)]">{fmtMoney(m.appreciation)}</span>
          <span className="text-[var(--ink-2)]">· {fmtPct(m.appreciation_pct)} from purchase</span>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className="bg-[var(--bg)] rounded-md p-2">
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-semibold">{label}</div>
      <div className={"font-display font-bold text-sm mt-0.5 tabular-nums " + (accent ? "text-[var(--primary)]" : "")}>{value}</div>
    </div>
  );
}

function PortfolioModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => initial ? { ...EMPTY, ...initial } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const body = { ...form };
    ["purchase_price", "rehab_cost", "current_value", "monthly_rent", "monthly_expenses", "mortgage_balance"].forEach((k) => {
      body[k] = body[k] === "" || body[k] == null ? 0 : Number(body[k]);
    });
    try {
      if (initial) await api.patch(`/portfolio/${initial.id}`, body);
      else await api.post("/portfolio", body);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="portfolio-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow">{initial ? "Edit property" : "Add to portfolio"}</div>
            <h2 className="font-display font-bold text-xl mt-1">{initial ? initial.property_address : "Track a property you own"}</h2>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="field-label">Property address *</label>
            <AddressAutocomplete
              name="property_address"
              placeholder="Start typing — we'll suggest verified addresses"
              inputClassName="input"
              dataTestid="port-property-address"
              value={form.property_address}
              onChange={(v) => setForm((p) => ({ ...p, property_address: v }))}
              onParsed={({ street, city, state, zip }) => setForm((prev) => ({
                ...prev,
                property_address: street || prev.property_address,
                city: city || prev.city,
                state: state || prev.state,
                zip_code: zip || prev.zip_code,
              }))}
            />
          </div>
          <div><label className="field-label">City</label><input className="input" value={form.city} onChange={set("city")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">State</label><input className="input" value={form.state} onChange={set("state")} /></div>
            <div><label className="field-label">ZIP</label><input className="input" value={form.zip_code} onChange={set("zip_code")} /></div>
          </div>
          <div>
            <label className="field-label">Strategy</label>
            <select className="select" value={form.strategy} onChange={set("strategy")} data-testid="port-strategy">
              {STRATEGIES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div><label className="field-label">Purchased on</label><input className="input" type="date" value={form.purchased_at} onChange={set("purchased_at")} /></div>

          <div className="md:col-span-2 divider my-2" />

          <div><label className="field-label">Purchase price</label><input className="input" type="number" min="0" value={form.purchase_price} onChange={set("purchase_price")} data-testid="port-purchase" /></div>
          <div><label className="field-label">Rehab cost</label><input className="input" type="number" min="0" value={form.rehab_cost} onChange={set("rehab_cost")} /></div>
          <div><label className="field-label">Current value</label><input className="input" type="number" min="0" value={form.current_value} onChange={set("current_value")} data-testid="port-value" /></div>
          <div><label className="field-label">Mortgage balance</label><input className="input" type="number" min="0" value={form.mortgage_balance} onChange={set("mortgage_balance")} /></div>
          <div><label className="field-label">Monthly rent</label><input className="input" type="number" min="0" value={form.monthly_rent} onChange={set("monthly_rent")} data-testid="port-rent" /></div>
          <div><label className="field-label">Monthly expenses</label><input className="input" type="number" min="0" value={form.monthly_expenses} onChange={set("monthly_expenses")} /></div>
          <div className="md:col-span-2"><label className="field-label">Notes</label><textarea className="textarea" rows={3} value={form.notes} onChange={set("notes")} /></div>

          {error && <div className="md:col-span-2 banner-err">{String(error)}</div>}
          <div className="md:col-span-2 flex justify-end gap-3 pt-3 border-t border-[var(--line)]">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-money" disabled={saving} data-testid="port-save-button">
              {saving ? "Saving…" : initial ? "Save changes" : "Add to portfolio"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
